/**
 * Markdown renderer for chat messages — chat-optimized typography.
 * Wraps react-markdown with remark-gfm and prose overrides for conversational
 * spacing. Links open in a new tab so the user stays in the SPA.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { HERALD_MESSAGE_SEGMENT_DATA_ATTR } from "@/lib/chat/heraldChatDomDataAttrs";

export interface ChatMarkdownProps {
  children: string;
  className?: string;
  /** When true, sets {@link HERALD_MESSAGE_SEGMENT_DATA_ATTR}=`markdown` on the prose root. */
  isMarkdownSegment?: boolean;
}

const PROSE_OVERRIDES = [
  "prose-p:leading-relaxed prose-p:my-1 prose-p:first:mt-0",
  "prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5",
  "prose-headings:my-2 prose-headings:font-semibold",
  "prose-pre:bg-muted prose-pre:border prose-pre:border-border prose-pre:rounded-lg prose-pre:text-sm",
  "prose-code:text-sm prose-code:before:content-none prose-code:after:content-none",
  "prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:font-normal",
  "prose-table:text-sm prose-th:px-3 prose-th:py-1.5 prose-td:px-3 prose-td:py-1.5",
  "prose-a:text-primary prose-a:underline-offset-2 prose-a:decoration-primary/30 hover:prose-a:decoration-primary",
  "prose-blockquote:border-border prose-blockquote:not-italic prose-blockquote:font-normal",
  "prose-hr:my-3 prose-hr:border-border",
] as const;

const PROSE_CLASSES = [
  "prose prose-sm text-sm dark:prose-invert max-w-none",
  ...PROSE_OVERRIDES,
].join(" ");

const ChatMarkdown = ({
  children,
  className,
  isMarkdownSegment,
}: ChatMarkdownProps) => {
  const combinedClassName = className
    ? `${PROSE_CLASSES} ${className}`
    : PROSE_CLASSES;

  return (
    <div
      className={combinedClassName}
      {...(isMarkdownSegment
        ? { [HERALD_MESSAGE_SEGMENT_DATA_ATTR]: "markdown" }
        : {})}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children: linkChildren, ...props }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
              {linkChildren}
            </a>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default ChatMarkdown;
